<script setup lang="ts">
  import { computed } from 'vue';
  import {
    SelectContent,
    SelectItem,
    SelectItemText,
    SelectPortal,
    SelectRoot,
    SelectTrigger,
    SelectViewport,
  } from 'reka-ui';
  import { Check } from 'lucide-vue-next';
  import type { SelectOption } from '@record-fill';
  import { IconImage } from '@/shared/ui';
  import ArrowDown from '@/shared/assets/icons/ArrowDown.svg';
  import {
    FILL_SELECT_BUTTON_CLASSES,
    FILL_SELECT_EMPTY_TEXT_CLASSES,
    FILL_SELECT_FILLED_TEXT_CLASSES,
  } from '../../../model/dropdown-select-classes';

  const props = defineProps<{
    options: SelectOption[];
    modelValue: string[];
    placeholder: string;
    disabled: boolean;
    readOnly?: boolean;
    label: string;
  }>();
  const emit = defineEmits<{ (e: 'update', value: string[]): void }>();

  // Reka throws on a SelectItem whose value is '', and the builder lets an author save a blank
  // alternative, so the items carry their index and only this component maps back to values.
  const selectedKeys = computed(() =>
    props.options.flatMap((option, index) => (props.modelValue.includes(option.value) ? [String(index)] : [])),
  );

  const selectedLabels = computed(() =>
    props.options.filter((option) => props.modelValue.includes(option.value)).map((option) => option.label),
  );
  const isEmpty = computed(() => selectedLabels.value.length === 0);
  const displayText = computed(() => selectedLabels.value.join(', '));

  function onUpdate(keys: unknown): void {
    const picked = new Set(Array.isArray(keys) ? keys.map(String) : []);
    const values = props.options.filter((_, index) => picked.has(String(index))).map((option) => option.value);
    emit('update', [...new Set(values)]);
  }
</script>

<template>
  <SelectRoot
    :model-value="selectedKeys"
    multiple
    :disabled="disabled || readOnly"
    @update:model-value="onUpdate"
  >
    <div class="relative w-full">
      <SelectTrigger
        :aria-label="label"
        :title="isEmpty ? undefined : displayText"
        :class="[
          'w-full text-left text-gray-07 focus:outline-none',
          readOnly ? '!cursor-default' : 'disabled:opacity-50 disabled:cursor-not-allowed',
          FILL_SELECT_BUTTON_CLASSES,
        ]"
        data-testid="fill-multi-select-trigger"
      >
        <span
          v-if="isEmpty"
          :class="['block w-full truncate text-gray-03', FILL_SELECT_EMPTY_TEXT_CLASSES]"
          data-select-placeholder
        >
          {{ placeholder }}
        </span>
        <span
          v-else
          :class="['block w-full truncate', FILL_SELECT_FILLED_TEXT_CLASSES]"
        >
          {{ displayText }}
        </span>
      </SelectTrigger>
      <IconImage
        :src="ArrowDown"
        alt="Seta dropdown"
        class="absolute top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 right-1.5"
      />
    </div>

    <SelectPortal>
      <SelectContent
        position="popper"
        :side-offset="4"
        align="start"
        class="z-[9999] w-[var(--reka-select-trigger-width)] max-h-[min(240px,var(--reka-select-content-available-height))] bg-white-01 border border-gray-02 rounded-2xl shadow-lg overflow-hidden"
        data-testid="fill-multi-select-list"
      >
        <SelectViewport class="p-3">
          <SelectItem
            v-for="(option, index) in options"
            :key="index"
            :value="String(index)"
            :text-value="option.label"
            class="group flex items-center gap-2 px-3 py-2 cursor-pointer text-sm font-normal text-gray-07 rounded-lg outline-none transition-colors duration-150 data-[highlighted]:bg-[var(--theme-secondary-tint)]"
          >
            <span
              aria-hidden="true"
              class="flex items-center justify-center shrink-0 w-4 h-4 rounded border border-[var(--color-light-gray-xx)] bg-white-01 group-data-[state=checked]:bg-[var(--theme-secondary)] group-data-[state=checked]:border-[var(--theme-secondary-ink)]"
            >
              <Check
                class="size-3 text-white hidden group-data-[state=checked]:block"
                :stroke-width="3"
              />
            </span>
            <SelectItemText class="truncate">
              {{ option.label }}
            </SelectItemText>
          </SelectItem>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
